import { Alert, AlertTitle, Box, Typography } from "@mui/material";

import AccessRequired from "./AccessRequired.jsx";
import ErrorState from "./ErrorState.jsx";

/**
 * The first rule, as one component: **a refusal is information, and it is not
 * an empty page.**
 *
 * This lived inside `apps/console/src/sections/Objects.jsx` — one screen out
 * of eight, holding the branch that the README calls the platform's most
 * load-bearing distinction. Every other screen re-decided it, and three of
 * them landed somewhere slightly different: `Portal` folds the two into one
 * `AlertTitle`, `Requests` switches severity but keeps one sentence, `Apps`
 * has no branch at all. None of those is wrong enough to notice; together
 * they are four stories about what a 403 means.
 *
 * ## The branch
 *
 * `403 not_in_manifest` (or `no_manifest`) naming a type and a tag means
 * **the app was never allowed to ask**. It is a fact about a declaration
 * somebody wrote down, and widening it is a change to a manifest.
 *
 * Anything else is a fact about a network or a bug, and goes to `ErrorState`.
 *
 * A person who lacks the access an app needs (`403 insufficient_access`) is a
 * third kind of refusal again, and it renders the missing-permissions screen
 * rather than either of these (LO-209).
 *
 * Zero rows from a request that *succeeded* is a fourth thing again — that is
 * `EmptyState`, and it never reaches this component, which is exactly the
 * point. Three different people's decisions, three different sentences.
 *
 * ## Rendered in the kernel's own words
 *
 * `body.detail` is the kernel's refusal, and it names the type, the tag and
 * what the app *does* declare. A message written here instead would be a
 * second, worse description of a decision this component did not make — and
 * it would be wrong the first time somebody widened a manifest. The extra
 * sentence below it says who to talk to, which is the one thing the kernel
 * cannot know.
 *
 * `isManifest` is read as a property rather than checked with `instanceof`.
 * A remote has its own module graph and therefore its own `KernelError`
 * class, so `instanceof` is false across that boundary for an object that is
 * a `KernelError` in every way that matters. That is a bug waiting in any
 * component a micro-app imports, and it costs nothing to not have it.
 */
export default function Refusal({
  failure,
  actor = "This app",
  onRetry,
  // Only for the access branch below: the token the missing-permissions screen
  // asks and requests with, when the app has one to give. Without it the screen
  // uses the session's own, which is what an app signed in through the kit has.
  token,
  // Only ever reaches the non-manifest branch. A caller may know something
  // about a particular failure that the kernel's status line does not say —
  // `no_directory` is answered correctly and is neither a refusal nor a
  // transport error — but a manifest refusal's heading is not negotiable,
  // because the whole value of it is that it reads the same everywhere.
  errorTitle,
  sx,
}) {
  if (!failure) return null;

  // The third kind of 403, and the only one with an answer on screen: the
  // *person* does not hold every tag this app needs (LO-209). Not the app's
  // manifest, so not the sentence below, and not "something went wrong" either.
  // `AccessGate` shows the same screen for the whole page; this is for an app
  // that met the refusal in its own error handling, which is every app built
  // before the gate existed, once it is rebuilt against this kit.
  if (failure.isAccessRequired) {
    const body = failure.body || {};
    return (
      <AccessRequired
        appType={body.app_type}
        title={actor === "This app" ? undefined : actor}
        missing={body.missing}
        detail={body.detail}
        doesNotProve={body.does_not_prove}
        canRequest={body.can_request !== false}
        token={token}
        onAllowed={onRetry}
      />
    );
  }

  if (!failure.isManifest) {
    return (
      <ErrorState
        failure={failure}
        title={errorTitle}
        onRetry={onRetry}
        sx={sx}
      />
    );
  }

  // Whichever of the kernel's structured fields this particular refusal
  // carries. `/actions/*` names an `action`, a `?tagged=` refusal names a
  // `type` and a `tag`, an access grant names `tags`.
  const named = ["type", "tag", "tags", "action", "parameter"]
    .map((key) => [key, failure.body?.[key]])
    .filter(([, value]) => value !== undefined && value !== null && value !== "");

  return (
    <Alert severity="warning" variant="outlined" sx={sx}>
      <AlertTitle>{actor} never asked for that</AlertTitle>
      <Box component="span">{failure.body?.detail || failure.message}</Box>
      <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
        {/* The sentence people need and the kernel cannot supply: which of
            the two limits did this, and therefore which document to change.
            Without it a manifest refusal reads as "you are not allowed",
            and the reader goes looking at their own groups. */}
        The manifest is what refused this, not your permissions. Widening it
        is a change to a declaration, not to your groups — and it is a
        decision a governor makes, not one this screen can make for you.
      </Typography>
      {named.length ? (
        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
          {/* The structured half of the same refusal. The kernel puts these
              beside `detail` so a caller does not have to parse a sentence,
              and a component that rendered only the sentence would be making
              every reader do exactly that. Only fields that are actually
              there are shown — there is no `declared` key, and inventing one
              because it would be convenient is how a UI starts describing an
              API that does not exist. */}
          {named.map(([key, value], index) => (
            <Box component="span" key={key}>
              {index ? " · " : ""}
              {key}: <code>{Array.isArray(value) ? value.join(", ") : value}</code>
            </Box>
          ))}
        </Typography>
      ) : null}
    </Alert>
  );
}
